import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { CvProcessorProvider, useCvProcessor } from "features/cv-processor/modal/CvProcessorContext";
import ConfirmModal from "shared/ConfirmModal";
import {
  Archive,
  ArrowLeft,
  ChevronRight,
  FileText,
  GripVertical,
  Plus,
  Trash2,
  X,
  Eye,
  Pencil,
  Printer,
  User,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  Github,
} from "lucide-react";
import {
  CvDocument,
  HeaderInfo,
  SectionKey,
  SkillGroup as ApiSkillGroup,
  WorkExperienceEntry as ApiWorkExperienceEntry,
  EducationEntry as ApiEducationEntry,
  ProjectEntry as ApiProjectEntry,
  createDocument,
  updateDocument,
} from "features/cv-processor/api/CvProcessorApi";
import { CvCategory, listCategories } from "features/cv-processor/api/CvCategoryApi";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const SECTION_KEYS: SectionKey[] = [
  "headline",
  "summary",
  "skills",
  "work_experience",
  "education",
  "projects",
];

export const SECTION_LABELS: Record<SectionKey, string> = {
  headline: "Professional Title",
  summary: "Professional Summary",
  work_experience: "Work Experience",
  skills: "Skills",
  education: "Education",
  projects: "Projects",
};

export const SECTION_PLACEHOLDERS: Record<SectionKey, string> = {
  headline: "e.g. Senior Backend Engineer | Python & FastAPI",
  summary:
    "Write 2-4 sentences positioning yourself for the target role. Focus on your specialization, years of experience, and key impact.",
  work_experience:
    "List each role with company, dates, title, and 3-5 impact bullets.\n\nAcme Corp, Karachi — Jan 2020 – Present\nSenior Backend Engineer\n- Built REST APIs serving 50k daily users\n- Reduced query latency by 35%",
  skills:
    "e.g. Python, FastAPI, PostgreSQL, Docker, Redis, AWS, Git",
  education:
    "e.g. FAST-NUCES Karachi — 2016-2020 — BSc Computer Science",
  projects:
    "List notable projects with tech stack and impact.\n\nOpen Source CLI Tool | Python, Click\n- 800+ GitHub stars, used by 50+ teams",
};

// ---------------------------------------------------------------------------
// ATS score display helpers (also imported by EnhancementsPage)
// ---------------------------------------------------------------------------

export function scoreColor(score: number): string {
  if (score >= 8) return "text-green-400";
  if (score >= 5) return "text-amber-400";
  return "text-red-400";
}

export function scoreBg(score: number): string {
  if (score >= 8) return "bg-green-400/10 border-green-500/30";
  if (score >= 5) return "bg-amber-400/10 border-amber-500/30";
  return "bg-red-400/10 border-red-500/30";
}

export function ScoreBadge({ score, size = "sm" }: { score: number; size?: "sm" | "md" }) {
  const textSize = size === "md" ? "text-sm" : "text-xs";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded-md border font-medium font-inter tabular-nums ${textSize} ${scoreColor(score)} ${scoreBg(score)}`}
    >
      {score}/10
    </span>
  );
}

// ---------------------------------------------------------------------------
// Structured section types, parsers, serializers
// ---------------------------------------------------------------------------

interface WorkExperienceEntry {
  id: string;
  company: string;
  location: string;
  period: string;
  position: string;
  techStack: string;
  bullets: string[];
}

interface EducationEntry {
  id: string;
  institution: string;
  degree: string;
  location: string;
  duration: string;
}

interface ProjectEntry {
  id: string;
  title: string;
  url: string;
  description: string;
  techStack: string;
  bullets: string[];
}

function splitBlocks(text: string): string[][] {
  return text
    .trim()
    .split(/\n\s*\n/)
    .map((b) => b.split("\n").map((l) => l.trim()).filter(Boolean))
    .filter((b) => b.length > 0);
}

function stripBulletPrefix(line: string): string {
  return line.replace(/^[•\-*]\s*/, "").trim();
}

function isBulletLine(line: string): boolean {
  return /^[•\-*]/.test(line);
}

function parseWorkExperience(text: string): WorkExperienceEntry[] {
  if (!text.trim()) return [];
  return splitBlocks(text)
    .map((lines, i) => {
      const [headerLine = "", ...afterHeader] = lines;
      const sepIdx = headerLine.indexOf(" — ");
      const companyWithLocation = sepIdx >= 0 ? headerLine.slice(0, sepIdx).trim() : headerLine;
      const period = sepIdx >= 0 ? headerLine.slice(sepIdx + 3).trim() : "";
      const commaIdx = companyWithLocation.indexOf(",");
      const company = commaIdx >= 0 ? companyWithLocation.slice(0, commaIdx).trim() : companyWithLocation;
      const location = commaIdx >= 0 ? companyWithLocation.slice(commaIdx + 1).trim() : "";
      // Find tech stack by label anywhere in afterHeader — avoids positional assumptions when position is empty
      const techIdx = afterHeader.findIndex((l) => /^(tech stack|technologies)\s*:/i.test(stripBulletPrefix(l)));
      const techLine = techIdx >= 0 ? afterHeader[techIdx] : "";
      const techStack = techLine ? stripBulletPrefix(techLine).replace(/^(tech stack|technologies)\s*:\s*/i, "").trim() : "";
      const withoutTech = techIdx >= 0 ? afterHeader.filter((_, idx) => idx !== techIdx) : afterHeader;
      // First non-bullet line is position; remaining lines are bullets
      const positionLine = withoutTech.find((l) => !isBulletLine(l)) ?? "";
      const bullets = withoutTech.filter(isBulletLine).map(stripBulletPrefix);
      if (!company && !positionLine && !period) return null;
      return {
        id: `exp_${i}`,
        company,
        location,
        period,
        position: positionLine,
        techStack,
        bullets: bullets.length > 0 ? bullets : ["", "", "", ""],
      };
    })
    .filter(Boolean) as WorkExperienceEntry[];
}

function serializeWorkExperience(entries: WorkExperienceEntry[]): string {
  return entries
    .filter((e) => e.company || e.position || e.period)
    .map((e) => {
      const companyWithLocation = [e.company, e.location].filter(Boolean).join(", ");
      const header = [companyWithLocation, e.period].filter(Boolean).join(" — ");
      const techLine = e.techStack?.trim() ? `Tech Stack: ${e.techStack.trim()}` : "";
      const bullets = e.bullets.filter((b) => b.trim()).map((b) => `- ${b.trim()}`);
      return [header, e.position, techLine, ...bullets].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

function parseEducation(text: string): EducationEntry[] {
  if (!text.trim()) return [];
  return splitBlocks(text)
    .map((lines, i) => {
      const [headerLine = "", degreeLineRaw = ""] = lines;
      const sepIdx = headerLine.indexOf(" — ");
      const institutionWithLocation = sepIdx >= 0 ? headerLine.slice(0, sepIdx).trim() : headerLine;
      const duration = sepIdx >= 0 ? headerLine.slice(sepIdx + 3).trim() : "";
      const commaIdx = institutionWithLocation.indexOf(",");
      const institution = commaIdx >= 0 ? institutionWithLocation.slice(0, commaIdx).trim() : institutionWithLocation;
      const location = commaIdx >= 0 ? institutionWithLocation.slice(commaIdx + 1).trim() : "";
      if (!degreeLineRaw && !institution) return null;
      return { id: `edu_${i}`, institution, degree: degreeLineRaw, location, duration };
    })
    .filter(Boolean) as EducationEntry[];
}

function serializeEducation(entries: EducationEntry[]): string {
  return entries
    .filter((e) => e.institution || e.degree)
    .map((e) => {
      const institutionWithLocation = [e.institution, e.location].filter(Boolean).join(", ");
      const header = [institutionWithLocation, e.duration].filter(Boolean).join(" — ");
      return [header, e.degree].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

function parseProjects(text: string): ProjectEntry[] {
  if (!text.trim()) return [];
  return splitBlocks(text)
    .map((lines, i) => {
      const [titleLine = "", ...rest] = lines;
      if (!titleLine) return null;
      let url = "";
      let techStack = "";
      const descLines: string[] = [];
      const bullets: string[] = [];
      for (const line of rest) {
        if (/^https?:\/\//.test(line)) {
          // Accumulate all URL lines (e.g. live demo + GitHub) joined by newline
          // so they each land on their own line when serialized back.
          url = url ? `${url}\n${line}` : line;
        } else if (/^tech stack\s*:/i.test(line)) {
          techStack = line.replace(/^tech stack\s*:\s*/i, "").trim();
        } else if (isBulletLine(line)) {
          bullets.push(stripBulletPrefix(line));
        } else {
          descLines.push(line);
        }
      }
      return {
        id: `proj_${i}`,
        title: titleLine,
        url,
        description: descLines.join(" "),
        techStack,
        bullets: bullets.length > 0 ? bullets : ["", "", ""],
      };
    })
    .filter(Boolean) as ProjectEntry[];
}

function serializeProjects(entries: ProjectEntry[]): string {
  return entries
    .filter((e) => e.title)
    .map((e) => {
      const techLine = e.techStack?.trim() ? `Tech Stack: ${e.techStack.trim()}` : "";
      const bullets = e.bullets.filter((b) => b.trim()).map((b) => `- ${b.trim()}`);
      return [e.title, e.url, techLine, e.description, ...bullets].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

interface SkillsEntry {
  id: string;
  heading: string;
  skills: string;
}

function parseSkills(text: string): SkillsEntry[] {
  if (!text.trim()) return [];
  return splitBlocks(text)
    .map((lines, i) => {
      const [rawHeading = "", ...rest] = lines;
      if (!rawHeading) return null;
      // Single-line block with no trailing colon and commas → flat skill list with no heading
      if (!rest.length && !rawHeading.endsWith(":") && rawHeading.includes(",")) {
        const skills = rawHeading.split(",").map((s) => s.trim()).filter(Boolean).join(", ");
        return { id: `skill_${i}`, heading: "", skills };
      }
      // Strip trailing colon so the form input shows "Languages" not "Languages:"
      const heading = rawHeading.replace(/:$/, "").trim();
      const skills = rest.map(stripBulletPrefix).filter(Boolean).join(", ");
      return { id: `skill_${i}`, heading, skills };
    })
    .filter(Boolean) as SkillsEntry[];
}

function serializeSkills(entries: SkillsEntry[]): string {
  return entries
    .filter((e) => e.heading || e.skills)
    .map((e) => {
      // Always output "Heading:" on line 1 and comma-separated skills on line 2.
      // This matches the format the AI receives and produces, and what the
      // scoring rubric grades on ("each group is exactly two lines").
      const heading = e.heading.replace(/:$/, "").trim();
      const skills = e.skills.split(",").map((s) => s.trim()).filter(Boolean).join(", ");
      return [heading ? `${heading}:` : "", skills].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

function newWorkExperienceEntry(): WorkExperienceEntry {
  return { id: `exp_${Date.now()}_${Math.random().toString(36).slice(2)}`, company: "", location: "", period: "", position: "", techStack: "", bullets: ["", "", "", ""] };
}
function newEducationEntry(): EducationEntry {
  return { id: `edu_${Date.now()}_${Math.random().toString(36).slice(2)}`, institution: "", degree: "", location: "", duration: "" };
}
function newProjectEntry(): ProjectEntry {
  return { id: `proj_${Date.now()}_${Math.random().toString(36).slice(2)}`, title: "", url: "", description: "", techStack: "", bullets: ["", "", ""] };
}
function newSkillsEntry(): SkillsEntry {
  return { id: `skill_${Date.now()}_${Math.random().toString(36).slice(2)}`, heading: "", skills: "" };
}

// ---------------------------------------------------------------------------
// API ↔ text conversion helpers (JSON arrays from API ↔ plain text for editors)
// ---------------------------------------------------------------------------

function apiSkillsToText(groups: ApiSkillGroup[]): string {
  if (!groups || !groups.length) return "";
  return groups
    .filter((g) => g.heading || (Array.isArray(g.skills) && g.skills.length))
    .map((g) => {
      const heading = g.heading?.trim() ? `${g.heading.trim()}:` : "";
      const skills = Array.isArray(g.skills) ? g.skills.join(", ") : String(g.skills || "");
      return [heading, skills].filter(Boolean).join("\n");
    })
    .join("\n\n");
}

function textToApiSkills(text: string): ApiSkillGroup[] {
  return parseSkills(text).map(({ heading, skills }) => ({
    heading,
    skills: skills.split(",").map((s) => s.trim()).filter(Boolean),
  }));
}

function apiWorkExpToText(entries: ApiWorkExperienceEntry[]): string {
  if (!entries || !entries.length) return "";
  return serializeWorkExperience(entries.map((e, i) => ({ id: `exp_${i}`, ...e })));
}

function textToApiWorkExp(text: string): ApiWorkExperienceEntry[] {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return parseWorkExperience(text).map(({ id: _id, ...rest }) => rest);
}

function apiEducationToText(entries: ApiEducationEntry[]): string {
  if (!entries || !entries.length) return "";
  return serializeEducation(
    entries.map((e, i) => ({
      id: `edu_${i}`,
      institution: e.institution,
      degree: e.degree,
      location: e.location,
      duration: e.duration,
    }))
  );
}

function textToApiEducation(text: string): ApiEducationEntry[] {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return parseEducation(text).map(({ id: _id, institution, degree, location, duration }) => ({
    institution,
    degree,
    location,
    duration,
  }));
}

function apiProjectsToText(entries: ApiProjectEntry[]): string {
  if (!entries || !entries.length) return "";
  return serializeProjects(entries.map((e, i) => ({ id: `proj_${i}`, ...e })));
}

function textToApiProjects(text: string): ApiProjectEntry[] {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return parseProjects(text).map(({ id: _id, ...rest }) => rest);
}

/** Get a section's content as plain text from a CvDocument (handles both string and JSON array). */
export function getDocSectionText(doc: CvDocument, key: SectionKey): string {
  const v = (doc as any)[key];
  if (Array.isArray(v)) {
    if (key === "skills") return apiSkillsToText(v);
    if (key === "work_experience") return apiWorkExpToText(v);
    if (key === "education") return apiEducationToText(v);
    if (key === "projects") return apiProjectsToText(v);
    return "";
  }
  return v || "";
}

/** Build the API payload value for a section key from plain-text draft content. */
export function sectionToApiPayload(key: SectionKey, text: string): any {
  if (key === "skills") return textToApiSkills(text);
  if (key === "work_experience") return textToApiWorkExp(text);
  if (key === "education") return textToApiEducation(text);
  if (key === "projects") return textToApiProjects(text);
  return text;
}

// ---------------------------------------------------------------------------
// Small components
// ---------------------------------------------------------------------------

function Spinner({ size = "md" }: { size?: "sm" | "md" }) {
  const cls = size === "sm" ? "w-4 h-4" : "w-6 h-6";
  return (
    <div
      className={`${cls} border-2 border-primary border-t-transparent rounded-full animate-spin`}
    />
  );
}

// ---------------------------------------------------------------------------
// CV Preview & PDF export — exact match to cv-processor-main design
// ---------------------------------------------------------------------------

export function CvPreview({ doc, hideContactInfo }: { doc: CvDocument; hideContactInfo?: boolean }) {
  const header = doc.header_info;

  const normalizeBulletText = (value: string) =>
    value
      .replace(/^[\s •‣⁃∙◦▪▫●○■▸▹•◦▪▫●○■▶▷\-*–—]+/u, "")
      .trim();

  const splitByBlankLines = (value: string) =>
    value
      .trim()
      .split(/\n\s*\n/)
      .map((entry) => entry.split("\n").map((line) => line.trim()).filter(Boolean))
      .filter((entry) => entry.length > 0);

  const renderSummary = (content: string) => {
    const items = content.split("\n").map((l) => l.trim()).filter(Boolean);
    return (
      <div className="space-y-1">
        {items.map((item, i) => (
          <p key={i} className="text-sm leading-6 text-zinc-700">{item}</p>
        ))}
      </div>
    );
  };

  const renderSkills = (content: string) => {
    const groups = splitByBlankLines(content);
    return (
      <div className="technical-skills-groups cv-entry-list">
        {groups.map((group, index) => {
          const [heading, ...items] = group;
          const skillsLine = items.map((item) => normalizeBulletText(item)).filter(Boolean).join(", ");
          return (
            <div key={`${heading}-${index}`} className="cv-entry space-y-0">
              <p className="cv-skills-block text-sm">
                <strong className="cv-skills-heading">{heading}</strong>
                {skillsLine ? (
                  <>
                    <br />
                    <span>{skillsLine}</span>
                  </>
                ) : null}
              </p>
            </div>
          );
        })}
      </div>
    );
  };

  const renderExperience = (content: string) => {
    const entries = splitByBlankLines(content);
    return (
      <div className="cv-entry-list cv-experience-list">
        {entries.map((entry, index) => {
          const [headerLine = "", roleLine = "", ...bullets] = entry;
          const [companyWithLocation = headerLine, dateRange = ""] = headerLine.split(" — ");
          const [companyName = companyWithLocation, ...locationParts] = companyWithLocation.split(",");
          const location = locationParts.join(",").trim();
          const displayRole = roleLine || companyName.trim();
          const displayCompany = roleLine ? companyName.trim() : "";
          const normalizedLines = bullets.map((l) => l.trim()).filter(Boolean);
          const techIndex = normalizedLines.findIndex((l) =>
            /^(technologies|tech stack)\s*:?/i.test(normalizeBulletText(l))
          );
          const technologiesLine = techIndex >= 0 ? normalizeBulletText(normalizedLines[techIndex]) : "";
          const bulletLines = normalizedLines.filter((_, li) => li !== techIndex);
          const techSepIdx = technologiesLine.indexOf(":");
          const parsedTechLabel = techSepIdx >= 0 ? technologiesLine.slice(0, techSepIdx).trim() : technologiesLine;
          const techLabel = !parsedTechLabel || parsedTechLabel === "Technologies" ? "Tech Stack" : parsedTechLabel;
          const techValue = techSepIdx >= 0 ? technologiesLine.slice(techSepIdx + 1).trim() : "";
          return (
            <div key={`${companyWithLocation}-${index}`} className="cv-entry space-y-0">
              <div className="cv-exp-header">
                {(displayCompany || location || dateRange) && (
                  <p className="cv-exp-meta text-sm flex justify-between items-baseline">
                    <span>
                      {displayCompany ? <span className="cv-exp-company">{displayCompany}</span> : null}
                      {location ? (
                        <>
                          {displayCompany ? <span className="cv-exp-sep"> - </span> : null}
                          <span className="cv-exp-location">{location}</span>
                        </>
                      ) : null}
                    </span>
                    {dateRange ? <span className="cv-exp-date">{dateRange}</span> : null}
                  </p>
                )}
                <p className="cv-exp-role text-sm font-semibold">{displayRole}</p>
              </div>
              {technologiesLine ? (
                <p className="cv-exp-tech text-sm">
                  <span className="cv-exp-tech-label">{techLabel}</span>
                  {techValue ? <span className="cv-exp-tech-value">: {techValue}</span> : null}
                </p>
              ) : null}
              <div className="space-y-0 cv-exp-bullets">
                {bulletLines.map((bullet, bi) => {
                  const text = normalizeBulletText(bullet);
                  if (!text) return null;
                  return (
                    <div key={`${bullet}-${index}-${bi}`} className="cv-exp-bullet-row text-sm">
                      <span className="cv-exp-bullet-marker" aria-hidden="true">•</span>
                      <span>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderEducation = (content: string) => {
    const entries = splitByBlankLines(content);
    return (
      <div className="space-y-3">
        {entries.map((entry, index) => {
          const [headerLine = "", degreeLine = ""] = entry;
          const [institutionWithLocation = headerLine, dateRange = ""] = headerLine.split(" — ");
          const [institutionName = institutionWithLocation, ...locationParts] = institutionWithLocation.split(",");
          const location = locationParts.join(",").trim();
          const displayDegree = degreeLine || institutionName.trim();
          const displayInstitution = degreeLine ? institutionName.trim() : "";
          return (
            <div key={index} className="space-y-0 cv-education-block">
              <p className="cv-edu-degree text-sm font-semibold">{displayDegree}</p>
              {(displayInstitution || location || dateRange) && (
                <p className="cv-edu-meta text-sm flex justify-between items-baseline">
                  <span>
                    {displayInstitution ? <span className="cv-edu-institution">{displayInstitution}</span> : null}
                    {location ? (
                      <>
                        {displayInstitution ? <span className="cv-edu-sep"> - </span> : null}
                        <span className="cv-edu-location">{location}</span>
                      </>
                    ) : null}
                  </span>
                  {dateRange ? <span className="cv-edu-date">{dateRange}</span> : null}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderProjects = (content: string) => {
    const entries = splitByBlankLines(content);
    return (
      <div className="cv-entry-list cv-projects-list [&>*:not(:first-child)]:!mt-4">
        {entries.map((entry, index) => {
          const [titleLine = "", ...rest] = entry;
          // Scan all lines after the title for a URL — aligns with how parseProjects works.
          const urlIdx = rest.findIndex((l) => /^https?:\/\//.test(l));
          const urlLine = urlIdx >= 0 ? rest[urlIdx] : "";
          const remaining = urlIdx >= 0 ? rest.filter((_, i) => i !== urlIdx) : rest;
          const bullets: string[] = [];
          const descLines: string[] = [];
          let techStackLine = "";
          for (const line of remaining) {
            if (/^[•\-*]/.test(line)) bullets.push(line);
            else if (/^tech stack\s*:/i.test(line)) techStackLine = line.replace(/^tech stack\s*:\s*/i, "").trim();
            else descLines.push(line);
          }
          const description = descLines.join(" ");
          return (
            <div key={`${titleLine}-${index}`} className="cv-entry space-y-0">
              <p className="cv-project-title text-sm font-semibold">{titleLine}</p>
              {urlLine ? (
                <a href={urlLine} target="_blank" rel="noreferrer" className="cv-project-link text-sm">
                  {urlLine}
                </a>
              ) : null}
              {techStackLine && (
                <p className="cv-exp-tech text-sm !mt-1">
                  <span className="cv-exp-tech-label">Tech Stack</span>
                  <span className="cv-exp-tech-value">: {techStackLine}</span>
                </p>
              )}
              {description && (
                <p className="text-sm text-zinc-600 !mt-1">{description}</p>
              )}
              <div className="space-y-0 cv-project-bullets">
                {bullets.map((bullet, bi) => {
                  const text = normalizeBulletText(bullet);
                  if (!text) return null;
                  return (
                    <div key={`${bullet}-${index}-${bi}`} className="cv-project-bullet-row text-sm">
                      <span className="cv-project-bullet-marker" aria-hidden="true">•</span>
                      <span>{text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const CV_SECTIONS: { key: SectionKey; label: string; render: (c: string) => React.ReactNode }[] = [
    { key: "summary", label: doc.headline ? doc.headline : "PROFESSIONAL SUMMARY", render: renderSummary },
    { key: "skills", label: "SKILLS", render: renderSkills },
    { key: "work_experience", label: "EXPERIENCE", render: renderExperience },
    { key: "education", label: "EDUCATION", render: renderEducation },
    { key: "projects", label: "PROJECTS", render: renderProjects },
  ];

  const makeHref = (url: string) =>
    url.startsWith("http") ? url : `https://${url}`;
  const displayUrl = (url: string) =>
    url.replace(/^https?:\/\//, "");

  return (
    <div className="cv-content">
      <div className="rounded-xl bg-white p-8 shadow-sm max-w-4xl mx-auto" style={{ fontFamily: '"EB Garamond", Garamond, "Adobe Garamond Pro", Georgia, serif' }}>
        <header className="cv-header mb-6 print:mb-4">
          <div className="relative flex items-center justify-center">
            <h2 className="text-center text-4xl font-bold tracking-wide text-zinc-900">
              {header?.name || <span className="text-zinc-400 italic text-base">No name — click Edit Header to add</span>}
            </h2>
          </div>
          {!hideContactInfo && (
            <p className="mt-1 text-center text-sm text-zinc-700">
              {[
                header?.location ? <span key="location">{header.location}</span> : null,
                header?.phone   ? <span key="phone">{header.phone}</span> : null,
                header?.email   ? <a key="email" href={`mailto:${header.email}`} className="underline text-blue-600">{header.email}</a> : null,
                header?.linkedin ? <a key="linkedin" href={makeHref(header.linkedin)} target="_blank" rel="noreferrer noopener" className="underline text-blue-600">{displayUrl(header.linkedin)}</a> : null,
                header?.github  ? <a key="github" href={makeHref(header.github)} target="_blank" rel="noreferrer noopener" className="underline text-blue-600">{displayUrl(header.github)}</a> : null,
              ].filter(Boolean).reduce<React.ReactNode[]>((acc, el, i) => i === 0 ? [el] : [...acc, <span key={`sep-${i}`} className="mx-1.5 text-zinc-400">•</span>, el], [])}
            </p>
          )}
        </header>

        <section className="space-y-5">
          {CV_SECTIONS.map(({ key, label, render }) => {
            const content = getDocSectionText(doc, key).trim();
            if (!content) return null;
            return (
              <article key={key} className="cv-section">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-zinc-900 pb-1">
                  <h2 className={`text-base font-bold tracking-wide${key === "summary" && doc.headline ? " headline-label" : ""}`}>{label}</h2>
                </div>
                {render(content)}
              </article>
            );
          })}
        </section>
      </div>
    </div>
  );
}

export function buildPrintHtml(doc: CvDocument, hideContactInfo?: boolean): string {
  const header = doc.header_info;

  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

  const makeHref = (url: string) =>
    url.startsWith("http") ? url : `https://${url}`;

  const normalizeBulletText = (value: string) =>
    value
      .replace(/^[\s •‣⁃∙◦▪▫●○■▸▹•◦▪▫●○■▶▷\-*–—]+/u, "")
      .trim();

  const splitByBlankLines = (value: string) =>
    value
      .trim()
      .split(/\n\s*\n/)
      .map((entry) => entry.split("\n").map((l) => l.trim()).filter(Boolean))
      .filter((entry) => entry.length > 0);

  const renderSummary = (content: string): string => {
    const items = content.split("\n").map((l) => l.trim()).filter(Boolean);
    return `<div class="cv-summary">${items.map((item) => `<p style="color:#3f3f46;margin:0">${esc(item)}</p>`).join("")}</div>`;
  };

  const renderSkills = (content: string): string => {
    const groups = splitByBlankLines(content);
    return `<div class="technical-skills-groups cv-entry-list">${groups.map((group) => {
      const [heading, ...items] = group;
      const skillsLine = items.map((item) => normalizeBulletText(item)).filter(Boolean).join(", ");
      return `<div class="cv-entry"><p class="cv-skills-block"><strong class="cv-skills-heading">${esc(heading)}</strong>${skillsLine ? `<br><span>${esc(skillsLine)}</span>` : ""}</p></div>`;
    }).join("")}</div>`;
  };

  const renderExperience = (content: string): string => {
    const entries = splitByBlankLines(content);
    return `<div class="cv-entry-list cv-experience-list">${entries.map((entry) => {
      const [headerLine = "", roleLine = "", ...bullets] = entry;
      const [companyWithLocation = headerLine, dateRange = ""] = headerLine.split(" — ");
      const [companyName = companyWithLocation, ...locationParts] = companyWithLocation.split(",");
      const location = locationParts.join(",").trim();
      const displayRole = roleLine || companyName.trim();
      const displayCompany = roleLine ? companyName.trim() : "";
      const normalizedLines = bullets.map((l) => l.trim()).filter(Boolean);
      const techIndex = normalizedLines.findIndex((l) =>
        /^(technologies|tech stack)\s*:?/i.test(normalizeBulletText(l))
      );
      const technologiesLine = techIndex >= 0 ? normalizeBulletText(normalizedLines[techIndex]) : "";
      const bulletLines = normalizedLines.filter((_, li) => li !== techIndex);
      const techSepIdx = technologiesLine.indexOf(":");
      const parsedTechLabel = techSepIdx >= 0 ? technologiesLine.slice(0, techSepIdx).trim() : technologiesLine;
      const techLabel = !parsedTechLabel || parsedTechLabel === "Technologies" ? "Tech Stack" : parsedTechLabel;
      const techValue = techSepIdx >= 0 ? technologiesLine.slice(techSepIdx + 1).trim() : "";
      const leftParts: string[] = [];
      if (displayCompany) leftParts.push(`<span class="cv-exp-company">${esc(displayCompany)}</span>`);
      if (location) { if (leftParts.length) leftParts.push(`<span class="cv-exp-sep"> - </span>`); leftParts.push(`<span class="cv-exp-location">${esc(location)}</span>`); }
      const hasMetaRow = !!(displayCompany || location || dateRange);
      return `<div class="cv-entry">
        <div class="cv-exp-header">
          ${hasMetaRow ? `<p class="cv-exp-meta"><span>${leftParts.join("")}</span>${dateRange ? `<span class="cv-exp-date">${esc(dateRange)}</span>` : ""}</p>` : ""}
          <p class="cv-exp-role">${esc(displayRole)}</p>
        </div>
        ${technologiesLine ? `<p class="cv-exp-tech"><span class="cv-exp-tech-label">${esc(techLabel)}</span>${techValue ? `<span class="cv-exp-tech-value">: ${esc(techValue)}</span>` : ""}</p>` : ""}
        <div class="cv-exp-bullets">${bulletLines.map((bullet) => {
          const text = normalizeBulletText(bullet);
          return text ? `<div class="cv-exp-bullet-row"><span class="cv-exp-bullet-marker">•</span><span>${esc(text)}</span></div>` : "";
        }).join("")}</div>
      </div>`;
    }).join("")}</div>`;
  };

  const renderEducation = (content: string): string => {
    const entries = splitByBlankLines(content);
    return entries.map((entry) => {
      const [headerLine = "", degreeLine = ""] = entry;
      const [institutionWithLocation = headerLine, dateRange = ""] = headerLine.split(" — ");
      const [institutionName = institutionWithLocation, ...locationParts] = institutionWithLocation.split(",");
      const location = locationParts.join(",").trim();
      const displayDegree = degreeLine || institutionName.trim();
      const displayInstitution = degreeLine ? institutionName.trim() : "";
      const leftParts: string[] = [];
      if (displayInstitution) leftParts.push(`<span class="cv-edu-institution">${esc(displayInstitution)}</span>`);
      if (location) { if (leftParts.length) leftParts.push(`<span class="cv-edu-sep"> - </span>`); leftParts.push(`<span class="cv-edu-location">${esc(location)}</span>`); }
      const hasMetaRow = !!(displayInstitution || location || dateRange);
      return `<div class="cv-education-block" style="margin-bottom:0.5rem">
        <p class="cv-edu-degree">${esc(displayDegree)}</p>
        ${hasMetaRow ? `<p class="cv-edu-meta"><span>${leftParts.join("")}</span>${dateRange ? `<span class="cv-edu-date">${esc(dateRange)}</span>` : ""}</p>` : ""}
      </div>`;
    }).join("");
  };

  const renderProjects = (content: string): string => {
    const entries = splitByBlankLines(content);
    return `<div class="cv-entry-list cv-projects-list">${entries.map((entry) => {
      const [titleLine = "", ...rest] = entry;
      // Scan all lines after the title for a URL — mirrors parseProjects behavior.
      const urlIdx = rest.findIndex((l) => /^https?:\/\//.test(l));
      const urlLine = urlIdx >= 0 ? rest[urlIdx] : "";
      const remaining = urlIdx >= 0 ? rest.filter((_, i) => i !== urlIdx) : rest;
      const bullets: string[] = [];
      const descLines: string[] = [];
      let techStackLine = "";
      for (const line of remaining) {
        if (/^[•\-*]/.test(line)) bullets.push(line);
        else if (/^tech stack\s*:/i.test(line)) techStackLine = line.replace(/^tech stack\s*:\s*/i, "").trim();
        else descLines.push(line);
      }
      const description = descLines.join(" ");
      return `<div class="cv-entry">
        <p class="cv-project-title">${esc(titleLine)}</p>
        ${urlLine ? `<a href="${esc(makeHref(urlLine))}" class="cv-project-link">${esc(urlLine)}</a>` : ""}
        ${techStackLine ? `<p class="cv-exp-tech" style="margin-top:0.1rem"><span class="cv-exp-tech-label">Tech Stack</span><span class="cv-exp-tech-value">: ${esc(techStackLine)}</span></p>` : ""}
        ${description ? `<p style="margin-top:0.1rem;color:#444;font-size:11pt">${esc(description)}</p>` : ""}
        <div class="cv-project-bullets">${bullets.map((bullet) => {
          const text = normalizeBulletText(bullet);
          return text ? `<div class="cv-project-bullet-row"><span class="cv-project-bullet-marker">•</span><span>${esc(text)}</span></div>` : "";
        }).join("")}</div>
      </div>`;
    }).join("")}</div>`;
  };

  const CV_SECTIONS: { key: SectionKey; label: string; render: (c: string) => string }[] = [
    { key: "summary", label: doc.headline ? doc.headline : "PROFESSIONAL SUMMARY", render: renderSummary },
    { key: "skills", label: "SKILLS", render: renderSkills },
    { key: "work_experience", label: "EXPERIENCE", render: renderExperience },
    { key: "education", label: "EDUCATION", render: renderEducation },
    { key: "projects", label: "PROJECTS", render: renderProjects },
  ];

  let sectionsHtml = "";
  for (const { key, label, render } of CV_SECTIONS) {
    const content = getDocSectionText(doc, key).trim();
    if (!content) continue;
    const headingClass = key === "summary" && doc.headline ? ` class="headline-label"` : "";
    sectionsHtml += `<article class="cv-section"><div class="cv-section-heading"><h2${headingClass}>${esc(label)}</h2></div>${render(content)}</article>`;
  }

  const contactSegments: string[] = [];
  if (!hideContactInfo) {
    if (header?.location) contactSegments.push(esc(header.location));
    if (header?.phone) contactSegments.push(esc(header.phone));
    if (header?.email) contactSegments.push(`<a href="mailto:${esc(header.email)}">${esc(header.email)}</a>`);
    if (header?.linkedin) contactSegments.push(`<a href="${esc(makeHref(header.linkedin))}">${esc(header.linkedin.replace(/^https?:\/\//, ""))}</a>`);
    if (header?.github) contactSegments.push(`<a href="${esc(makeHref(header.github))}">${esc(header.github.replace(/^https?:\/\//, ""))}</a>`);
  }

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>${esc(header?.name || doc.title)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap" rel="stylesheet">
<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
body {
  font-family: "EB Garamond", Garamond, "Adobe Garamond Pro", serif;
  font-size: 11pt;
  color: #0f0f0f;
  background: #fff;
  padding: 0.5in;
  --cv-line-height: 1.25;
  --cv-line-gap: 0.12rem;
  --cv-entry-gap: 0.72rem;
  --cv-link-color: #1155cc;
}
a { color: #1155cc; text-decoration: underline; }

/* Header */
.cv-header { margin-bottom: 1.5rem; }
.cv-header h2 {
  font-size: 20pt;
  font-weight: 700;
  text-transform: capitalize;
  font-variant: small-caps;
  letter-spacing: 0.02em;
  text-align: center;
  color: #111;
}
.cv-header p {
  font-size: 10pt;
  text-align: center;
  margin-top: 0.15rem;
  line-height: var(--cv-line-height);
}
.cv-header .headline { color: #555; margin-top: 0.1rem; }

/* Sections */
.cv-section { margin-bottom: 1rem; break-inside: auto; }
.cv-section-heading {
  margin-bottom: 0.35rem;
  padding-bottom: 0.08rem;
  border-bottom: 1px solid #000;
  break-after: avoid;
}
.cv-section-heading h2 {
  font-size: 12pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0;
  color: #0f0f0f;
  font-family: "EB Garamond", Garamond, serif;
}
.cv-section-heading h2.headline-label {
  text-transform: none;
}

/* All section text */
.cv-section p, .cv-section a, .cv-section div, .cv-section span {
  font-size: 11pt;
  line-height: var(--cv-line-height);
}

/* Bullet rows (summary) */
.cv-bullet-list { }
.cv-bullet-row { display: flex; align-items: flex-start; gap: 0.35rem; line-height: var(--cv-line-height); margin-top: var(--cv-line-gap); }
.cv-bullet-row:first-child { margin-top: 0; }
.cv-bullet-marker { flex: 0 0 0.55rem; }

/* Entry list */
.cv-entry-list { display: grid; row-gap: var(--cv-entry-gap); break-inside: auto; }
.cv-entry { break-inside: avoid; }
.cv-experience-list { row-gap: 0.58rem; }

/* Experience */
.cv-exp-header { margin-bottom: 0.1rem; }
.cv-exp-meta { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 0.02rem; line-height: var(--cv-line-height); }
.cv-exp-role { font-weight: 700; }
.cv-exp-company { font-weight: 700; }
.cv-exp-location, .cv-exp-sep, .cv-exp-date { font-weight: 400; }
.cv-exp-tech { margin: 0 0 0.02rem 0; line-height: 1.24; }
.cv-exp-tech-label { font-weight: 700; color: #000; }
.cv-exp-tech-value { font-weight: 400; color: #0f0f0f; }
.cv-exp-bullets { margin-top: 0.06rem; }
.cv-exp-bullet-row { display: flex; align-items: flex-start; gap: 0.34rem; line-height: 1.22; margin-top: var(--cv-line-gap); }
.cv-exp-bullet-row:first-child { margin-top: 0; }
.cv-exp-bullet-marker { flex: 0 0 0.5rem; line-height: 1.22; }

/* Education */
.cv-education-block { margin-top: 0.02rem; }
.cv-edu-degree { font-weight: 700; margin-bottom: 0.02rem; }
.cv-edu-meta { display: flex; justify-content: space-between; align-items: baseline; line-height: 1.24; }
.cv-edu-institution, .cv-edu-location, .cv-edu-sep, .cv-edu-date { font-weight: 400; }

/* Skills */
.technical-skills-groups.cv-entry-list { row-gap: 0; }
.technical-skills-groups .cv-entry { margin-bottom: 0.12rem; }
.cv-skills-block { margin: 0; line-height: var(--cv-line-height); }
.cv-skills-heading { display: inline-block; font-weight: 700; color: #000; letter-spacing: 0; }

/* Projects */
.cv-projects-list { row-gap: 0.64rem; }
.cv-project-title { font-weight: 700; margin: 0; }
.cv-project-link { display: inline-block; margin-top: 0.02rem; margin-bottom: 0; color: #1155cc; text-decoration: underline; line-height: 1.24; }
.cv-project-tech { margin: 0.34rem 0 0.02rem 0; line-height: 1.24; }
.cv-project-tech-label { font-weight: 700; color: #000; }
.cv-project-tech-value { font-weight: 400; color: #0f0f0f; }
.cv-project-bullets { margin-top: 0.02rem; }
.cv-project-bullet-row { display: flex; align-items: flex-start; gap: 0.34rem; line-height: 1.22; margin-top: 0; }
.cv-project-bullet-marker { flex: 0 0 0.5rem; line-height: 1.22; }

@page { size: auto; margin: 0; }
@media print { body { padding: 0.5in; } }
</style>
</head>
<body>
<header class="cv-header">
  ${header?.name ? `<h2>${esc(header.name)}</h2>` : ""}
  ${contactSegments.length ? `<p>${contactSegments.join(" • ")}</p>` : ""}
</header>
${sectionsHtml}
</body>
</html>`;
}

// Shared by every "Download / Print" button across CV Management, Enhancements,
// and the job-pipeline EnhancedCvModal — opens a blank window, writes the print
// HTML into it, and prints once the web font has loaded (or after a timeout,
// so a stalled font request — e.g. fonts.googleapis.com blocked on a corporate
// network — can never prevent print() from firing at all).
export function openPrintWindow(html: string): void {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(html);
  w.document.close();
  w.focus();
  Promise.race([
    w.document.fonts.ready,
    new Promise((resolve) => setTimeout(resolve, 2000)),
  ]).then(() => w.print());
}

// ---------------------------------------------------------------------------
// Header info edit panel
// ---------------------------------------------------------------------------

const headerSchema = Yup.object({
  name: Yup.string(),
  location: Yup.string(),
  email: Yup.string().email("Invalid email"),
  phone: Yup.string(),
  linkedin: Yup.string(),
  github: Yup.string(),
});

function HeaderEditPanel({
  value,
  onSave,
  saving,
}: {
  value: HeaderInfo | null;
  onSave: (info: HeaderInfo) => void;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);

  const formik = useFormik({
    initialValues: {
      name: value?.name || "",
      location: value?.location || "",
      email: value?.email || "",
      phone: value?.phone || "",
      linkedin: value?.linkedin || "",
      github: value?.github || "",
    },
    validationSchema: headerSchema,
    onSubmit: (vals) => {
      onSave(vals as HeaderInfo);
      setOpen(false);
    },
  });

  const hasAny = !!(value?.name || value?.location || value?.phone || value?.email || value?.linkedin || value?.github);

  const handleOpen = () => {
    formik.resetForm({
      values: {
        name: value?.name || "",
        location: value?.location || "",
        email: value?.email || "",
        phone: value?.phone || "",
        linkedin: value?.linkedin || "",
        github: value?.github || "",
      },
    });
    setOpen(true);
  };

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground font-inter transition-colors text-left"
      >
        <User className="w-3.5 h-3.5 shrink-0" />
        <span>
          {hasAny
            ? [value?.name, value?.location, value?.phone, value?.email, value?.linkedin, value?.github].filter(Boolean).join("  ·  ")
            : "+ Add contact info (name, email, LinkedIn, GitHub)"}
        </span>
        <Pencil className="w-3 h-3 shrink-0 opacity-40 ml-1" />
      </button>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit} className="rounded-2xl border border-primary/40 bg-surface p-4 space-y-3">
      <p className="text-xs font-medium text-muted-foreground font-inter">Contact info</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(
          [
            { key: "name", label: "Full name", icon: User, placeholder: "Jane Doe" },
            { key: "location", label: "Location", icon: MapPin, placeholder: "Lahore, Pakistan" },
            { key: "email", label: "Email", icon: Mail, placeholder: "jane@example.com" },
            { key: "phone", label: "Phone", icon: Phone, placeholder: "+1 555 123 4567" },
            { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "linkedin.com/in/janedoe" },
            { key: "github", label: "GitHub", icon: Github, placeholder: "github.com/janedoe" },
          ] as const
        ).map(({ key, label, icon: Icon, placeholder }) => (
          <div key={key} className="flex items-center gap-2">
            <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <input
              type="text"
              {...formik.getFieldProps(key)}
              placeholder={placeholder}
              aria-label={label}
              className="flex-1 bg-transparent border-b border-border text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:border-primary py-1"
            />
          </div>
        ))}
      </div>
      {formik.touched.email && formik.errors.email && (
        <p className="text-xs text-red-400 font-inter">{formik.errors.email}</p>
      )}
      <div className="flex justify-end gap-3 pt-1">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground hover:text-foreground font-inter"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="text-xs text-primary hover:text-primary font-inter disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Structured section editors
// ---------------------------------------------------------------------------

export function WorkExperienceEditor({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled?: boolean;
}) {
  const [entries, setEntries] = useState<WorkExperienceEntry[]>(() => {
    const parsed = parseWorkExperience(value);
    return parsed.length > 0 ? parsed : [newWorkExperienceEntry()];
  });
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      const parsed = parseWorkExperience(value);
      setEntries(parsed.length > 0 ? parsed : [newWorkExperienceEntry()]);
    }
  }, [value]);

  const update = (next: WorkExperienceEntry[]) => {
    setEntries(next);
    const serialized = serializeWorkExperience(next);
    lastValueRef.current = serialized;
    onChange(serialized);
  };

  const handleContainerBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) onBlur();
  };

  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    const sourceId = dragIdRef.current;
    setDragOverId(null);
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const from = entries.findIndex((e) => e.id === sourceId);
    const to = entries.findIndex((e) => e.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...entries];
    next.splice(to, 0, next.splice(from, 1)[0]);
    update(next);
  };

  return (
    <div className="p-4 space-y-4" onBlur={handleContainerBlur}>
      {entries.map((entry, ei) => (
        <div
          key={entry.id}
          onDragOver={(e) => { e.preventDefault(); if (dragIdRef.current !== entry.id) setDragOverId(entry.id); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={() => handleDrop(entry.id)}
          className={`rounded-2xl border bg-surface p-4 space-y-3 transition-colors ${dragOverId === entry.id ? "border-primary" : "border-border"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                draggable
                onDragStart={() => { dragIdRef.current = entry.id; }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider">
                Experience {ei + 1}
              </span>
            </div>
            {entries.length > 1 && (
              <button
                onClick={() => {
                  const next = entries.filter((e) => e.id !== entry.id);
                  update(next.length > 0 ? next : [newWorkExperienceEntry()]);
                }}
                disabled={disabled}
                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-muted-foreground font-inter mb-1 block">Company</label>
              <input
                type="text"
                value={entry.company}
                onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, company: e.target.value } : en))}
                disabled={disabled}
                placeholder="e.g. Acme Corp"
                className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-inter mb-1 block">Location</label>
              <input
                type="text"
                value={entry.location}
                onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, location: e.target.value } : en))}
                disabled={disabled}
                placeholder="e.g. Karachi"
                className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-inter mb-1 block">Period</label>
              <input
                type="text"
                value={entry.period}
                onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, period: e.target.value } : en))}
                disabled={disabled}
                placeholder="e.g. Jan 2020 – Present"
                className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Job Position</label>
            <input
              type="text"
              value={entry.position}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, position: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. Senior Backend Engineer"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Tech Stack</label>
            <input
              type="text"
              value={entry.techStack}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, techStack: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. Python, FastAPI, PostgreSQL, Docker, Redis"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground font-inter block">Job Details</label>
            {entry.bullets.map((bullet, bi) => (
              <div key={bi} className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm shrink-0">•</span>
                <input
                  type="text"
                  value={bullet}
                  onChange={(e) => {
                    const newBullets = [...entry.bullets];
                    newBullets[bi] = e.target.value;
                    update(entries.map((en) => en.id === entry.id ? { ...en, bullets: newBullets } : en));
                  }}
                  disabled={disabled}
                  placeholder={`Detail ${bi + 1}`}
                  className="flex-1 px-3 py-1.5 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                />
                {entry.bullets.length > 1 && (
                  <button
                    onClick={() => update(entries.map((en) => en.id === entry.id ? { ...en, bullets: entry.bullets.filter((_, i) => i !== bi) } : en))}
                    disabled={disabled}
                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {entry.bullets.length < 6 && (
              <button
                onClick={() => update(entries.map((en) => en.id === entry.id ? { ...en, bullets: [...entry.bullets, ""] } : en))}
                disabled={disabled}
                className="text-xs text-primary hover:text-primary font-inter flex items-center gap-1 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add detail
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        onClick={() => update([...entries, newWorkExperienceEntry()])}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border rounded-xl text-sm text-muted-foreground hover:text-foreground hover:border-primary font-inter transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Add Work Experience
      </button>
    </div>
  );
}

export function EducationEditor({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled?: boolean;
}) {
  const [entries, setEntries] = useState<EducationEntry[]>(() => {
    const parsed = parseEducation(value);
    return parsed.length > 0 ? parsed : [newEducationEntry()];
  });
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      const parsed = parseEducation(value);
      setEntries(parsed.length > 0 ? parsed : [newEducationEntry()]);
    }
  }, [value]);

  const update = (next: EducationEntry[]) => {
    setEntries(next);
    const serialized = serializeEducation(next);
    lastValueRef.current = serialized;
    onChange(serialized);
  };

  const handleContainerBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) onBlur();
  };

  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    const sourceId = dragIdRef.current;
    setDragOverId(null);
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const from = entries.findIndex((e) => e.id === sourceId);
    const to = entries.findIndex((e) => e.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...entries];
    next.splice(to, 0, next.splice(from, 1)[0]);
    update(next);
  };

  return (
    <div className="p-4 space-y-4" onBlur={handleContainerBlur}>
      {entries.map((entry, ei) => (
        <div
          key={entry.id}
          onDragOver={(e) => { e.preventDefault(); if (dragIdRef.current !== entry.id) setDragOverId(entry.id); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={() => handleDrop(entry.id)}
          className={`rounded-2xl border bg-surface p-4 space-y-3 transition-colors ${dragOverId === entry.id ? "border-primary" : "border-border"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                draggable
                onDragStart={() => { dragIdRef.current = entry.id; }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider">
                Education {ei + 1}
              </span>
            </div>
            {entries.length > 1 && (
              <button
                onClick={() => {
                  const next = entries.filter((e) => e.id !== entry.id);
                  update(next.length > 0 ? next : [newEducationEntry()]);
                }}
                disabled={disabled}
                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Institute Name</label>
            <input
              type="text"
              value={entry.institution}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, institution: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. FAST-NUCES"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground font-inter mb-1 block">Location</label>
              <input
                type="text"
                value={entry.location}
                onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, location: e.target.value } : en))}
                disabled={disabled}
                placeholder="e.g. Karachi"
                className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground font-inter mb-1 block">Duration</label>
              <input
                type="text"
                value={entry.duration}
                onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, duration: e.target.value } : en))}
                disabled={disabled}
                placeholder="e.g. 2016 – 2020"
                className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Degree</label>
            <input
              type="text"
              value={entry.degree}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, degree: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. BSc Computer Science"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
        </div>
      ))}
      <button
        onClick={() => update([...entries, newEducationEntry()])}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border rounded-xl text-sm text-muted-foreground hover:text-foreground hover:border-primary font-inter transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Add Education
      </button>
    </div>
  );
}

export function ProjectsEditor({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled?: boolean;
}) {
  const [entries, setEntries] = useState<ProjectEntry[]>(() => {
    const parsed = parseProjects(value);
    return parsed.length > 0 ? parsed : [newProjectEntry()];
  });
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      const parsed = parseProjects(value);
      setEntries(parsed.length > 0 ? parsed : [newProjectEntry()]);
    }
  }, [value]);

  const update = (next: ProjectEntry[]) => {
    setEntries(next);
    const serialized = serializeProjects(next);
    lastValueRef.current = serialized;
    onChange(serialized);
  };

  const handleContainerBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) onBlur();
  };

  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    const sourceId = dragIdRef.current;
    setDragOverId(null);
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const from = entries.findIndex((e) => e.id === sourceId);
    const to = entries.findIndex((e) => e.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...entries];
    next.splice(to, 0, next.splice(from, 1)[0]);
    update(next);
  };

  return (
    <div className="p-4 space-y-4" onBlur={handleContainerBlur}>
      {entries.map((entry, ei) => (
        <div
          key={entry.id}
          onDragOver={(e) => { e.preventDefault(); if (dragIdRef.current !== entry.id) setDragOverId(entry.id); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={() => handleDrop(entry.id)}
          className={`rounded-2xl border bg-surface p-4 space-y-3 transition-colors ${dragOverId === entry.id ? "border-primary" : "border-border"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                draggable
                onDragStart={() => { dragIdRef.current = entry.id; }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider">
                Project {ei + 1}
              </span>
            </div>
            {entries.length > 1 && (
              <button
                onClick={() => {
                  const next = entries.filter((e) => e.id !== entry.id);
                  update(next.length > 0 ? next : [newProjectEntry()]);
                }}
                disabled={disabled}
                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Project Title</label>
            <input
              type="text"
              value={entry.title}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, title: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. Open Source CLI Tool"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">URL <span className="text-muted-foreground">(optional)</span></label>
            <input
              type="text"
              value={entry.url}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, url: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. https://github.com/user/project"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Tech Stack</label>
            <input
              type="text"
              value={entry.techStack}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, techStack: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. React, Node.js, PostgreSQL"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Description</label>
            <input
              type="text"
              value={entry.description}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, description: e.target.value } : en))}
              disabled={disabled}
              placeholder="Brief description of the project"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs text-muted-foreground font-inter block">Key Points</label>
            {entry.bullets.map((bullet, bi) => (
              <div key={bi} className="flex items-center gap-2">
                <span className="text-muted-foreground text-sm shrink-0">•</span>
                <input
                  type="text"
                  value={bullet}
                  onChange={(e) => {
                    const newBullets = [...entry.bullets];
                    newBullets[bi] = e.target.value;
                    update(entries.map((en) => en.id === entry.id ? { ...en, bullets: newBullets } : en));
                  }}
                  disabled={disabled}
                  placeholder={`Point ${bi + 1}`}
                  className="flex-1 px-3 py-1.5 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
                />
                {entry.bullets.length > 1 && (
                  <button
                    onClick={() => update(entries.map((en) => en.id === entry.id ? { ...en, bullets: entry.bullets.filter((_, i) => i !== bi) } : en))}
                    disabled={disabled}
                    className="p-1 text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
            {entry.bullets.length < 6 && (
              <button
                onClick={() => update(entries.map((en) => en.id === entry.id ? { ...en, bullets: [...entry.bullets, ""] } : en))}
                disabled={disabled}
                className="text-xs text-primary hover:text-primary font-inter flex items-center gap-1 transition-colors"
              >
                <Plus className="w-3 h-3" />
                Add point
              </button>
            )}
          </div>
        </div>
      ))}
      <button
        onClick={() => update([...entries, newProjectEntry()])}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border rounded-xl text-sm text-muted-foreground hover:text-foreground hover:border-primary font-inter transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Add Project
      </button>
    </div>
  );
}

export function SkillsEditor({
  value,
  onChange,
  onBlur,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  disabled?: boolean;
}) {
  const [entries, setEntries] = useState<SkillsEntry[]>(() => {
    const parsed = parseSkills(value);
    return parsed.length > 0 ? parsed : [newSkillsEntry()];
  });
  const lastValueRef = useRef(value);

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      const parsed = parseSkills(value);
      setEntries(parsed.length > 0 ? parsed : [newSkillsEntry()]);
    }
  }, [value]);

  const update = (next: SkillsEntry[]) => {
    setEntries(next);
    const serialized = serializeSkills(next);
    lastValueRef.current = serialized;
    onChange(serialized);
  };

  const handleContainerBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) onBlur();
  };

  const dragIdRef = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDrop = (targetId: string) => {
    const sourceId = dragIdRef.current;
    setDragOverId(null);
    dragIdRef.current = null;
    if (!sourceId || sourceId === targetId) return;
    const from = entries.findIndex((e) => e.id === sourceId);
    const to = entries.findIndex((e) => e.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...entries];
    next.splice(to, 0, next.splice(from, 1)[0]);
    update(next);
  };

  return (
    <div className="p-4 space-y-4" onBlur={handleContainerBlur}>
      {entries.map((entry, ei) => (
        <div
          key={entry.id}
          onDragOver={(e) => { e.preventDefault(); if (dragIdRef.current !== entry.id) setDragOverId(entry.id); }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={() => handleDrop(entry.id)}
          className={`rounded-2xl border bg-surface p-4 space-y-3 transition-colors ${dragOverId === entry.id ? "border-primary" : "border-border"}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                draggable
                onDragStart={() => { dragIdRef.current = entry.id; }}
                onDragEnd={() => { dragIdRef.current = null; setDragOverId(null); }}
                className="cursor-grab active:cursor-grabbing p-0.5 text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                <GripVertical className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium text-muted-foreground font-inter uppercase tracking-wider">
                Section {ei + 1}
              </span>
            </div>
            {entries.length > 1 && (
              <button
                onClick={() => {
                  const next = entries.filter((e) => e.id !== entry.id);
                  update(next.length > 0 ? next : [newSkillsEntry()]);
                }}
                disabled={disabled}
                className="p-1 text-muted-foreground hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Heading</label>
            <input
              type="text"
              value={entry.heading}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, heading: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. Languages, Frameworks, Tools"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground font-inter mb-1 block">Skills <span className="text-muted-foreground">(comma-separated)</span></label>
            <input
              type="text"
              value={entry.skills}
              onChange={(e) => update(entries.map((en) => en.id === entry.id ? { ...en, skills: e.target.value } : en))}
              disabled={disabled}
              placeholder="e.g. Python, FastAPI, PostgreSQL, Docker"
              className="w-full px-3 py-2 bg-surface-muted border border-border rounded-lg text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
            />
          </div>
        </div>
      ))}
      <button
        onClick={() => update([...entries, newSkillsEntry()])}
        disabled={disabled}
        className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-border rounded-xl text-sm text-muted-foreground hover:text-foreground hover:border-primary font-inter transition-colors disabled:opacity-50"
      >
        <Plus className="w-4 h-4" />
        Add Skills Section
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New document modal
// ---------------------------------------------------------------------------

const newDocSchema = Yup.object({
  title: Yup.string().trim().required("Title is required"),
  categoryId: Yup.mixed<number | "">(),
});

function NewDocumentModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (doc: CvDocument) => void;
}) {
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const formik = useFormik({
    initialValues: { title: "", categoryId: "" as number | "" },
    validationSchema: newDocSchema,
    onSubmit: async (vals, { setSubmitting }) => {
      setApiError("");
      try {
        const doc = await createDocument({
          title: vals.title.trim(),
          category_id: vals.categoryId !== "" ? Number(vals.categoryId) : null,
        });
        onCreate(doc);
      } catch (e: any) {
        setApiError(e.response?.data?.detail || "Could not create document");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const activeCategories = categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-lg bg-surface border border-border rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground font-inter">
            New CV
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                CV title
              </label>
              <input
                type="text"
                {...formik.getFieldProps("title")}
                placeholder="e.g. Senior Backend Engineer - Acme Corp"
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              />
              {formik.touched.title && formik.errors.title && (
                <p className="mt-1 text-xs text-red-400 font-inter">{formik.errors.title}</p>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">
                CV Category <span className="text-muted-foreground font-normal">(optional — enables universal skills pool)</span>
              </label>
              <select
                name="categoryId"
                value={formik.values.categoryId}
                onChange={(e) => formik.setFieldValue("categoryId", e.target.value === "" ? "" : Number(e.target.value))}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
                className="w-full px-3 py-2.5 bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                <option value="">— No category —</option>
                {activeCategories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {apiError && (
              <p className="text-sm text-red-400 font-inter">{apiError}</p>
            )}
          </div>

          <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={formik.isSubmitting}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={formik.isSubmitting || !formik.values.title.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              {formik.isSubmitting ? <Spinner size="sm" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document list view
// ---------------------------------------------------------------------------

function DocumentList() {
  const { docs, docsLoading, docsError, archiving, pendingArchive, openDoc, setShowNewModal, requestArchive, confirmArchive, cancelArchive } = useCvProcessor();
  const [categoryMap, setCategoryMap] = useState<Record<number, string>>({});
  const navigate = useNavigate();
  const archivedPath = localStorage.getItem("authUserType") === "admin"
    ? "/admin/cv-processor/archived"
    : "/employee/cv-processor/archived";

  useEffect(() => {
    listCategories()
      .then((cats) => {
        const map: Record<number, string> = {};
        cats.forEach((c) => { map[c.id] = c.name; });
        setCategoryMap(map);
      })
      .catch(() => {});
  }, []);

  return (
    <>
    {pendingArchive && (
      <ConfirmModal
        title="Archive CV"
        message={`Archive "${pendingArchive.title}"? It will disappear from your CV list and any job-enhancement pickers — you can restore it from the Archived CVs page.`}
        confirmLabel="Archive"
        variant="danger"
        confirming={archiving === pendingArchive.id}
        onConfirm={confirmArchive}
        onCancel={cancelArchive}
      />
    )}
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-inter">
            My CVs
          </h1>
          <p className="text-sm text-muted-foreground font-inter mt-1">
            Create and manage CVs. Use the Enhancements page to tailor a CV for a specific job.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(archivedPath)}
            className="inline-flex items-center gap-2 px-3 py-2.5 text-muted-foreground hover:text-foreground border border-border hover:border-border text-sm font-medium font-inter rounded-xl transition-colors whitespace-nowrap"
          >
            <Archive className="w-4 h-4" />
            Archived
          </button>
          <button
            onClick={() => setShowNewModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            New CV
          </button>
        </div>
      </div>

      {docsError && (
        <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter">
          {docsError}
        </div>
      )}

      {docsLoading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : docs.length === 0 ? (
        <div className="rounded-2xl border border-border bg-surface/30 py-20 text-center">
          <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground font-inter text-sm">No CVs yet.</p>
          <button
            onClick={() => setShowNewModal(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium font-inter rounded-xl transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create your first CV
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border overflow-hidden">
          <table className="w-full text-left text-sm font-inter">
            <thead className="bg-surface-muted/80 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium w-28 hidden md:table-cell">
                  ATS Score
                </th>
                <th className="px-4 py-3 font-medium w-40 hidden md:table-cell">
                  Updated
                </th>
                <th className="px-4 py-3 font-medium w-16 text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {docs.map((doc) => (
                <tr
                  key={doc.id}
                  onClick={() => openDoc(doc)}
                  className="bg-surface/30 hover:bg-surface-muted transition-colors cursor-pointer"
                >
                  <td className="px-4 py-3">
                    <p className="text-foreground font-medium">{doc.title}</p>
                    {doc.category_id && categoryMap[doc.category_id] && (
                      <p className="text-xs text-muted-foreground mt-0.5">{categoryMap[doc.category_id]}</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {doc.holistic_review?.overall_score != null ? (
                      <ScoreBadge score={doc.holistic_review.overall_score} />
                    ) : (
                      <span className="text-xs text-muted-foreground font-inter">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground text-xs hidden md:table-cell">
                    {doc.updated_at
                      ? new Date(doc.updated_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={(e) => requestArchive(doc, e)}
                      disabled={archiving === doc.id}
                      className="p-2 text-muted-foreground hover:text-amber-400 transition-colors disabled:opacity-50"
                      title="Archive"
                    >
                      {archiving === doc.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <Archive className="w-4 h-4" />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Document editor view
// ---------------------------------------------------------------------------

function DocumentEditor({
  initialDoc,
  onBack,
}: {
  initialDoc: CvDocument;
  onBack: () => void;
}) {
  const [doc, setDoc] = useState<CvDocument>(initialDoc);
  const [activeSection, setActiveSection] = useState<SectionKey>("headline");
  const [draftContent, setDraftContent] = useState<Record<SectionKey, string>>(
    () =>
      SECTION_KEYS.reduce((acc, key) => {
        acc[key] = getDocSectionText(initialDoc, key);
        return acc;
      }, {} as Record<SectionKey, string>)
  );
  const [dirtyKeys, setDirtyKeys] = useState<Set<SectionKey>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(doc.title);
  const [categories, setCategories] = useState<CvCategory[]>([]);
  const [savingCategory, setSavingCategory] = useState(false);
  const [activeView, setActiveView] = useState<"edit" | "preview">("edit");
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    listCategories().then(setCategories).catch(() => {});
  }, []);

  const flashSuccess = (msg: string) => {
    setSaveSuccess(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSaveSuccess(""), 4000);
  };

  const handleCategoryChange = async (newCategoryId: number | null) => {
    setSavingCategory(true);
    setError("");
    try {
      const updated = await updateDocument(doc.id, { category_id: newCategoryId });
      setDoc(updated);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not change category");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleSaveHeader = async (info: HeaderInfo) => {
    setSaving(true);
    try {
      const updated = await updateDocument(doc.id, { header_info: info });
      setDoc(updated);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    openPrintWindow(buildPrintHtml(doc));
  };

  const currentContent = draftContent[activeSection];
  const isDirty = dirtyKeys.has(activeSection);

  // Save draft content to DB when user blurs the textarea, then score in background
  const handleBlur = async () => {
    if (!isDirty) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateDocument(doc.id, {
        [activeSection]: sectionToApiPayload(activeSection, currentContent),
      });
      setDoc(updated);
      setDirtyKeys((prev) => {
        const next = new Set(prev);
        next.delete(activeSection);
        return next;
      });
      flashSuccess("Saved!");
    } catch (e: any) {
      setError(e.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleContentChange = (value: string) => {
    setDraftContent((prev) => ({ ...prev, [activeSection]: value }));
    setDirtyKeys((prev) => new Set(prev).add(activeSection));
  };

  const saveTitle = async () => {
    if (!draftTitle.trim()) return;
    setSaving(true);
    try {
      const updated = await updateDocument(doc.id, { title: draftTitle.trim() });
      setDoc(updated);
    } catch {
      setDraftTitle(doc.title);
    } finally {
      setSaving(false);
      setEditingTitle(false);
    }
  };

  const sectionHasContent = (key: SectionKey) =>
    !!(draftContent[key] || "").trim();

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground font-inter transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          All CVs
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveTitle();
                    if (e.key === "Escape") {
                      setDraftTitle(doc.title);
                      setEditingTitle(false);
                    }
                  }}
                  autoFocus
                  className="text-2xl font-bold text-foreground font-inter bg-transparent border-b border-primary outline-none flex-1"
                />
                <button
                  onClick={saveTitle}
                  disabled={saving}
                  className="text-xs text-primary hover:text-primary font-inter"
                >
                  Save
                </button>
                <button
                  onClick={() => { setDraftTitle(doc.title); setEditingTitle(false); }}
                  className="text-xs text-muted-foreground hover:text-foreground font-inter"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <h1
                className="text-2xl font-bold text-foreground font-inter cursor-pointer hover:text-primary transition-colors"
                onClick={() => setEditingTitle(true)}
                title="Click to rename"
              >
                {doc.title}
              </h1>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={handlePrint}
              title="Export / Print CV"
              className="inline-flex items-center gap-2 px-3 py-2 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-sm font-medium font-inter rounded-xl transition-colors"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Header contact info */}
        <div className="mt-3">
          <HeaderEditPanel
            value={doc.header_info}
            onSave={handleSaveHeader}
            saving={saving}
          />
        </div>

        {/* Category selector */}
        <div className="mt-3 flex items-center gap-2">
          <label className="text-xs text-muted-foreground font-inter shrink-0">Category:</label>
          <select
            value={doc.category_id ?? ""}
            onChange={(e) => handleCategoryChange(e.target.value === "" ? null : Number(e.target.value))}
            disabled={savingCategory}
            className="flex-1 max-w-xs px-2.5 py-1.5 bg-surface-muted border border-border rounded-lg text-xs text-foreground font-inter focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50"
          >
            <option value="">— No category —</option>
            {categories.filter((c) => c.is_active && !c.is_system && !c.is_skill_family).map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {!doc.category_id && (
            <span className="text-[10px] text-amber-500/70 font-inter">Skills pool inactive</span>
          )}
        </div>

      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-inter flex items-start gap-2">
          <X className="w-4 h-4 mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      {saveSuccess && (
        <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-inter">
          {saveSuccess}
        </div>
      )}

      {/* Edit / Preview toggle + ATS score row */}
      <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-1 p-1 bg-surface-muted rounded-xl w-fit border border-border">
        <button
          onClick={() => setActiveView("edit")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
            activeView === "edit"
              ? "bg-surface text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit
        </button>
        <button
          onClick={() => setActiveView("preview")}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
            activeView === "preview"
              ? "bg-surface text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview
        </button>
      </div>

      {/* Overall ATS score (from last enhancement run) */}
      {doc.holistic_review?.overall_score != null && doc.job_description && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground font-inter">Overall ATS</span>
          <ScoreBadge score={doc.holistic_review.overall_score} size="md" />
        </div>
      )}
      </div>

      {activeView === "preview" ? (
        <CvPreview doc={doc} />
      ) : (
        /* Editor layout */
        <div className="flex gap-6">
          {/* Sidebar: section nav */}
          <div className="w-48 shrink-0 space-y-1">
            {SECTION_KEYS.map((key) => {
              const isActive = activeSection === key;
              const hasContent = sectionHasContent(key);

              return (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-inter transition-colors text-left ${
                    isActive
                      ? "bg-primary/10 text-primary border border-primary/30"
                      : "text-muted-foreground hover:text-foreground hover:bg-surface-muted border border-transparent"
                  }`}
                >
                  <span className="flex-1 truncate">
                    {SECTION_LABELS[key]}
                  </span>
                  {hasContent && !isActive && (
                    <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-40" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Main: editor */}
          <div className="flex-1 min-w-0 space-y-4">
            <div className="rounded-2xl border border-border bg-surface/50 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <h2 className="text-sm font-semibold text-foreground font-inter">
                  {SECTION_LABELS[activeSection]}
                </h2>
                <div className="flex items-center gap-2">
                  {saving && <Spinner size="sm" />}
                  <button
                    onClick={handleBlur}
                    disabled={!isDirty || saving}
                    className="inline-flex items-center px-3 py-1.5 bg-secondary hover:bg-secondary/80 disabled:opacity-30 disabled:cursor-not-allowed text-secondary-foreground text-xs font-medium font-inter rounded-lg transition-colors"
                  >
                    Save
                  </button>
                </div>
              </div>
              {activeSection === "work_experience" ? (
                <WorkExperienceEditor
                  value={currentContent}
                  onChange={handleContentChange}
                  onBlur={handleBlur}
                  disabled={saving}
                />
              ) : activeSection === "education" ? (
                <EducationEditor
                  value={currentContent}
                  onChange={handleContentChange}
                  onBlur={handleBlur}
                  disabled={saving}
                />
              ) : activeSection === "projects" ? (
                <ProjectsEditor
                  value={currentContent}
                  onChange={handleContentChange}
                  onBlur={handleBlur}
                  disabled={saving}
                />
              ) : activeSection === "skills" ? (
                <SkillsEditor
                  value={currentContent}
                  onChange={handleContentChange}
                  onBlur={handleBlur}
                  disabled={saving}
                />
              ) : (
                <textarea
                  value={currentContent}
                  onChange={(e) => handleContentChange(e.target.value)}
                  onBlur={handleBlur}
                  placeholder={SECTION_PLACEHOLDERS[activeSection]}
                  rows={activeSection === "headline" ? 3 : 12}
                  className="w-full px-4 py-3 bg-transparent text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none resize-none"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Root page component
// ---------------------------------------------------------------------------

function CvProcessorInner() {
  const { view, activeDoc, showNewModal, setShowNewModal, handleCreate, backToList } = useCvProcessor();

  if (view === "editor" && activeDoc) {
    return <DocumentEditor initialDoc={activeDoc} onBack={backToList} />;
  }

  return (
    <>
      <DocumentList />
      {showNewModal && (
        <NewDocumentModal
          onClose={() => setShowNewModal(false)}
          onCreate={handleCreate}
        />
      )}
    </>
  );
}

export default function CvProcessorPage() {
  return (
    <CvProcessorProvider>
      <CvProcessorInner />
    </CvProcessorProvider>
  );
}
